import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Save,
  Truck,
  Package,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { deliveryJournalAPI, salesOrdersAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import { formatDateOnlyFr } from '../utils/dateFormat';
import { todayYMD, toYMD, getWeekDays } from '../utils/calendarWeek';

const defaultForm = {
  sales_order_id: '',
  delivered_at: '',
};

const VentesJournalLivraisons = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);
  /** null = toutes les dates (après recherche) ; sinon filtre sur ce jour civil (Casablanca). */
  const [selectedDeliveredYMD, setSelectedDeliveredYMD] = useState(() => todayYMD());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedOrderItems, setSelectedOrderItems] = useState([]);
  const [loadingOrderItems, setLoadingOrderItems] = useState(false);

  // Fetch deliveries
  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      const res = await deliveryJournalAPI.getDeliveryJournal();
      setDeliveries(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement du journal de livraisons');
    }
    setLoading(false);
  };

  // Fetch sales orders for dropdown
  const fetchSalesOrders = async () => {
    try {
      const res = await salesOrdersAPI.getSalesOrders();
      const orders = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setSalesOrders(orders);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchDeliveries();
    fetchSalesOrders();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    // Handle both event objects and option objects from ModernSelect
    let name, value;
    
    if (e && e.target) {
      // Event object (for regular inputs)
      name = e.target.name;
      value = e.target.value;
    } else if (e && e.value !== undefined) {
      // Option object (for ModernSelect)
      name = 'sales_order_id'; // ModernSelect only handles sales_order_id
      value = e.value;
    } else {
      return; // Invalid input
    }
    
    setForm((f) => ({ ...f, [name]: value }));
    
    // If sales_order_id changed, fetch the order items
    if (name === 'sales_order_id' && value) {
      fetchOrderItems(value);
    }
  };

  // Fetch order items when an order is selected
  const fetchOrderItems = async (orderId) => {
    setLoadingOrderItems(true);
    try {
      const res = await salesOrdersAPI.getSalesOrder(orderId);
      setSelectedOrderItems(res.data.items || []);
    } catch (err) {
      console.error('Error fetching order items:', err);
      setSelectedOrderItems([]);
    } finally {
      setLoadingOrderItems(false);
    }
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await deliveryJournalAPI.updateDeliveryJournal(editingId, form);
        toast.success('Livraison modifiée');
      } else {
        await deliveryJournalAPI.createDeliveryJournal(form);
        toast.success('Livraison ajoutée');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchDeliveries();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (delivery) => {
    setForm({
      sales_order_id: delivery.sales_order_id,
      delivered_at: delivery.delivered_at ? delivery.delivered_at.slice(0, 16) : '',
    });
    setEditingId(delivery.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette livraison ?')) return;
    try {
      await deliveryJournalAPI.deleteDeliveryJournal(id);
      toast.success('Livraison supprimée');
      fetchDeliveries();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  // Helper to get sales order label (fallback when API row has no join yet)
  const getSalesOrderInfo = useCallback((salesOrderId) => {
    const order = salesOrders.find((o) => String(o.id) === String(salesOrderId));
    return order ? `Commande #${order.id}` : String(salesOrderId);
  }, [salesOrders]);

  const weekDays = useMemo(() => getWeekDays(weekOffset), [weekOffset]);

  const searchFilteredDeliveries = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return deliveries;
    return deliveries.filter((delivery) => {
      const idStr = String(delivery.sales_order_id ?? '');
      const name = (delivery.client_name || '').toLowerCase();
      const phone = (delivery.client_phone || '').toLowerCase();
      const orderInfo = getSalesOrderInfo(delivery.sales_order_id).toLowerCase();
      return (
        orderInfo.includes(q) ||
        idStr.includes(q) ||
        name.includes(q) ||
        phone.includes(q) ||
        String(delivery.delivered_at || '').toLowerCase().includes(q)
      );
    });
  }, [deliveries, searchTerm, getSalesOrderInfo]);

  const filteredDeliveries = useMemo(() => {
    if (selectedDeliveredYMD == null) return searchFilteredDeliveries;
    return searchFilteredDeliveries.filter((d) => toYMD(d.delivered_at) === selectedDeliveredYMD);
  }, [searchFilteredDeliveries, selectedDeliveredYMD]);

  const deliveriesWithoutDate = useMemo(
    () => deliveries.filter((d) => !toYMD(d.delivered_at)),
    [deliveries]
  );

  const todayYmdStr = todayYMD();
  const todayDeliveries = useMemo(
    () => deliveries.filter((d) => toYMD(d.delivered_at) === todayYmdStr),
    [deliveries, todayYmdStr]
  );
  const thisMonthDeliveries = useMemo(() => {
    const [y, m] = todayYmdStr.split('-').map(Number);
    return deliveries.filter((d) => {
      const ymd = toYMD(d.delivered_at);
      if (!ymd) return false;
      const [dy, dm] = ymd.split('-').map(Number);
      return dy === y && dm === m;
    });
  }, [deliveries, todayYmdStr]);

  const totalOrders = useMemo(
    () => new Set(deliveries.map((d) => d.sales_order_id)).size,
    [deliveries]
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'MAD' }).format(amount);
  };

  const salesOrderOptions = salesOrders.map(order => ({
    value: order.id.toString(),
    label: `Commande #${order.id} - ${order.client_name || 'Client'} - ${formatCurrency(order.total || 0)}`
  }));

  const tableColumns = [
    {
      key: 'sales_order_id',
      label: 'Commande / Client',
      render: (value, delivery) => (
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 bg-primary-100 rounded-lg flex items-center justify-center">
            <Package className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-neutral-900">Commande #{value}</div>
            <div className="text-sm text-neutral-700 truncate" title={delivery.client_name || ''}>
              {delivery.client_name || '—'}
            </div>
            <div className="text-sm text-neutral-500">{delivery.client_phone || '—'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'delivered_at',
      label: 'Date de livraison',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDateOnlyFr(value)}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, delivery) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(delivery)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(delivery)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(delivery.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Journal des Livraisons (Ventes)</h1>
          <p className="text-neutral-600">Gérez le journal des livraisons</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des livraisons..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter une livraison
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Livraisons</p>
              <p className="text-2xl font-bold text-neutral-900">{deliveries.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Aujourd'hui</p>
              <p className="text-2xl font-bold text-success-600">{todayDeliveries.length}</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Ce Mois</p>
              <p className="text-2xl font-bold text-warning-600">{thisMonthDeliveries.length}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Commandes Livrées</p>
              <p className="text-2xl font-bold text-info-600">{totalOrders}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Semaine (comme Todos) : jours + aperçu des livraisons par date */}
      <ModernCard className="p-4 sm:p-5 rounded-2xl border border-teal-100 bg-white shadow-md overflow-x-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setWeekOffset((w) => w - 1)}
              className="p-2 rounded-xl border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-700"
              aria-label="Semaine précédente"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setWeekOffset((w) => w + 1)}
              className="p-2 rounded-xl border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-700"
              aria-label="Semaine suivante"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm sm:text-base font-semibold text-neutral-800 text-center flex-1 min-w-[12rem]">
            {weekDays[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} —{' '}
            {weekDays[6].toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <div className="flex items-center gap-2">
            {selectedDeliveredYMD != null && (
              <ModernButton type="button" variant="secondary" size="sm" onClick={() => setSelectedDeliveredYMD(null)}>
                Toutes les dates
              </ModernButton>
            )}
            <ModernButton type="button" variant="secondary" size="sm" onClick={() => setWeekOffset(0)}>
              Aujourd&apos;hui
            </ModernButton>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          Cliquez sur un jour pour n&apos;afficher dans la liste que les livraisons prévues ce jour-là (date de livraison).
        </p>
        <div className="grid grid-cols-7 gap-1 sm:gap-2 min-w-[640px] sm:min-w-0">
          {weekDays.map((day) => {
            const ymd = toYMD(day);
            const dayDeliveries = deliveries.filter((d) => toYMD(d.delivered_at) === ymd);
            const isToday = todayYMD() === ymd;
            const isSelected = selectedDeliveredYMD === ymd;
            return (
              <button
                key={ymd}
                type="button"
                onClick={() => setSelectedDeliveredYMD((prev) => (prev === ymd ? null : ymd))}
                className={`rounded-xl border-2 p-2 sm:p-3 text-left transition-all duration-200 flex flex-col min-h-[120px] sm:min-h-[140px] ${
                  isSelected
                    ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200 shadow-md'
                    : isToday
                      ? 'border-emerald-300 bg-emerald-50/80 hover:border-emerald-400'
                      : 'border-neutral-200 bg-neutral-50/80 hover:border-neutral-300 hover:bg-white'
                }`}
              >
                <span className="text-[10px] sm:text-xs font-semibold text-neutral-500 uppercase">
                  {day.toLocaleDateString('fr-FR', { weekday: 'short' })}
                </span>
                <span
                  className={`text-lg sm:text-2xl font-black tabular-nums leading-tight ${
                    isToday ? 'text-emerald-700' : 'text-neutral-900'
                  }`}
                >
                  {day.getDate()}
                </span>
                <span className="text-[10px] text-neutral-400 mb-1">
                  {dayDeliveries.length} livraison{dayDeliveries.length !== 1 ? 's' : ''}
                </span>
                <div className="flex-1 space-y-0.5 overflow-hidden">
                  {dayDeliveries.slice(0, 3).map((d) => {
                    const label = d.client_name
                      ? `#${d.sales_order_id} · ${d.client_name}`
                      : `#${d.sales_order_id}`;
                    return (
                      <div
                        key={d.id}
                        className="text-[9px] sm:text-[10px] leading-tight text-neutral-700 truncate rounded px-0.5 bg-white/60 border border-neutral-100"
                        title={label}
                      >
                        {label}
                      </div>
                    );
                  })}
                  {dayDeliveries.length > 3 && (
                    <span className="text-[9px] font-semibold text-teal-600">+{dayDeliveries.length - 3}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {deliveriesWithoutDate.length > 0 && (
          <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            {deliveriesWithoutDate.length} livraison{deliveriesWithoutDate.length !== 1 ? 's' : ''} sans date de
            livraison valide (non comptées par jour ci-dessus).
          </p>
        )}
      </ModernCard>

      {/* Deliveries Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des livraisons</h3>
            <p className="text-sm text-neutral-600">
              {filteredDeliveries.length} livraison{filteredDeliveries.length !== 1 ? 's' : ''}
              {selectedDeliveredYMD != null
                ? ` · ${formatDateOnlyFr(selectedDeliveredYMD)}`
                : ' · toutes dates'}
              {searchTerm.trim() ? ' (recherche active)' : ''}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        {filteredDeliveries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50/80 px-6 py-12 text-center">
            <Truck className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
            <p className="text-neutral-700 font-medium">
              Aucune livraison pour cette sélection.
            </p>
            <p className="text-sm text-neutral-500 mt-1 mb-4">
              Changez de jour, réinitialisez la recherche ou affichez toutes les dates.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {selectedDeliveredYMD != null && (
                <ModernButton type="button" variant="secondary" size="sm" onClick={() => setSelectedDeliveredYMD(null)}>
                  Toutes les dates
                </ModernButton>
              )}
              {searchTerm.trim() && (
                <ModernButton type="button" variant="secondary" size="sm" onClick={() => setSearchTerm('')}>
                  Effacer la recherche
                </ModernButton>
              )}
            </div>
          </div>
        ) : (
          <ModernTable columns={tableColumns} data={filteredDeliveries} className="w-full" />
        )}
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier la livraison' : 'Ajouter une livraison'}
        size="full"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernSelect
              label="Commande"
              name="sales_order_id"
              value={salesOrderOptions.find(opt => opt.value === form.sales_order_id) || null}
              onChange={handleChange}
              options={salesOrderOptions}
              required
            />

            <ModernInput
              label="Date de livraison"
              name="delivered_at"
              type="datetime-local"
              value={form.delivered_at}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
              required
            />
          </div>

          {/* Display order items when an order is selected */}
          {form.sales_order_id && (
            <div className="space-y-4 border-t pt-4 mt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Produits de la commande</h3>
              </div>
              
              {loadingOrderItems ? (
                <div className="text-center py-8">
                  <ModernLoadingSpinner size="sm" />
                  <p className="text-sm text-gray-500 mt-2">Chargement des produits...</p>
                </div>
              ) : selectedOrderItems && selectedOrderItems.length > 0 ? (
                <div className="space-y-2">
                  {selectedOrderItems.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">{item.product_name}</div>
                        <div className="text-sm text-gray-600">
                          Quantité: {item.quantity} | Prix: {formatCurrency(item.price)} | Total: {formatCurrency(item.quantity * item.price)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Aucun produit dans cette commande
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default VentesJournalLivraisons; 